import { useState, useRef, useEffect, memo, useCallback } from 'react';
import RichTextEditor from './RichTextEditor';

/**
 * A RichTextEditor that auto-saves the current value on a debounce.
 * Fixes the "PUT-per-keystroke" storm on fields like Proposal Scope Notes
 * where the previous code called `await api.put(...)` on every keystroke,
 * flooding the backend and re-rendering the entire proposal panel each time.
 *
 * The field owns its value locally so keystrokes don't re-render the parent.
 * A debounced timer flushes the value to `onSave` after the user pauses
 * typing (default 500ms), or on unmount, whichever comes first.
 *
 * Usage:
 *   <AutoSaveRichTextField
 *     initialValue={selected.notes_scope || ''}
 *     resetKey={selected.id}                  // re-seed on record switch
 *     onSave={(v) => updateField('notes_scope', v)}
 *   />
 */
const DEFAULT_DEBOUNCE_MS = 500;

function AutoSaveRichTextField({
  initialValue = '',
  resetKey,
  onSave,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  ...rest
}) {
  const [value, setValue] = useState(initialValue || '');
  const savedRef = useRef(initialValue || '');
  const timerRef = useRef(null);

  // Re-seed when the caller signals a switch to a different record.
  // See IsolatedRichTextField for the rationale on excluding initialValue.
  useEffect(() => {
    setValue(initialValue || '');
    savedRef.current = initialValue || '';
  }, [resetKey]);

  const flush = useCallback((next) => {
    if (next === savedRef.current) return;
    savedRef.current = next;
    try { onSave?.(next); } catch { /* caller handles */ }
  }, [onSave]);

  const handleChange = useCallback((v) => {
    setValue(v);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flush(v), debounceMs);
  }, [flush, debounceMs]);

  // On unmount (e.g., panel close), flush any pending change
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  return <RichTextEditor value={value} onChange={handleChange} {...rest} />;
}

export default memo(AutoSaveRichTextField);
